import {
  CircularProgress,
  DialogTitle,
  Divider,
  Input,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  Box,
} from "@mui/material";
import {
  CloseIconWrapper,
  CreateBoardWrapper,
  FontGrayNormal,
  ModalIconWrapper,
  ModalTitle,
  StyledContainedButton,
  StyledDialog,
  StyledFormControl,
  StyledInputLabel,
  StyledTextField,
} from "./style";
import {
  CloseIcon,
  CreateFolderIcon,
  UploadImageIcon,
} from "../../assets/Icons";
import { CreateTeamValueProps } from "../../utils/propTypes";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createTeamValidationSchema } from "../../utils/formValidationSchema";
import { ErrorMessage } from "../../pageComponents/auth/style";
import {
  createTeamLoadingSelector,
  membersListSelector,
  openCreateTeamModalSelector,
} from "../../store/metadata/selector";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  LogoUploadButton,
  LogoUploadButtonAfter,
  SquareContainer,
  UploadedImage,
} from "../../pageComponents/settings/style";
import { useTheme } from "styled-components";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { membersArray } from "../../store/metadata/types";
import { userDetailsSelector } from "../../store/account/selector";

export interface Member {
  label: string;
  id: string;
  email: string;
}

const CreateTeamModal = () => {
  const theme = useTheme();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateTeamValueProps>({
    resolver: yupResolver(createTeamValidationSchema),
    defaultValues: {
      name: "",
      members: [],
    },
  });
  const membersList = useSelector(membersListSelector);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoErrorMessage, setLogoErrorMessage] = useState<boolean>(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const dispatch = useDispatch();
  const loading = useSelector(createTeamLoadingSelector);
  const openModal = useSelector(openCreateTeamModalSelector);
  const userDetails = useSelector(userDetailsSelector);

  useEffect(() => {
    if (membersList?.members) {
      const updatedMembers = membersList?.members
        ?.filter(
          (member: any) =>
            member?.acceptedStatus === "ACCEPTED" &&
            member?._id !== userDetails?._id
        )
        ?.map((member: membersArray) => ({
          label: member.fullName,
          id: member._id,
          email: member?.userEmail ?? member?.email,
        }));
      setTeamMembers(updatedMembers);
    }
  }, [membersList]);

  const onSubmit = (data: CreateTeamValueProps) => {
    if (!selectedFile) {
      setLogoErrorMessage(true);
    } else {
      const members = data?.members?.map((item) => ({
        email: item?.userEmail ?? item?.email,
        accepted: true,
        isOwner: false,
      }));
      dispatch(
        MetaDataSagaActions.createTeam({
          name: data.name,
          logo: selectedFile,
          members: members || [],
          handleCloseModal: handleCloseModal,
        })
      );
    }
  };

  const handleCloseModal = () => {
    setLogoErrorMessage(false);
    setSelectedFile(null);
    reset();
    dispatch(MetaDataReducerAction.setOpenCreateTeamModal(false));
  };

  const profileImageInputRef = useRef<HTMLInputElement | null>(null);

  const handleProfileImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setLogoErrorMessage(false);
    }
  };

  const handleProfileUploadButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    profileImageInputRef.current?.click();
  };

  return (
    <StyledDialog
      onClose={handleCloseModal}
      open={openModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ padding: "1.875rem 1.875rem 1.25rem 1.875rem" }}>
        <Stack direction="row" gap="0.75rem" alignItems="start">
          <ModalIconWrapper>
            <CreateFolderIcon fill="white" />
          </ModalIconWrapper>
          <Stack>
            <ModalTitle>Create Team</ModalTitle>
            <FontGrayNormal>
              Add a team name, logo, and select members to get started.
            </FontGrayNormal>
          </Stack>
        </Stack>
        <CloseIconWrapper aria-label="close" onClick={handleCloseModal}>
          <CloseIcon />
        </CloseIconWrapper>
      </DialogTitle>
      <Divider sx={{ borderColor: "#E3E6EA" }} />
      <CreateBoardWrapper>
        <StyledFormControl fullWidth>
          <StyledInputLabel htmlFor="name">Team Name</StyledInputLabel>
          <StyledTextField
            id="name"
            variant="outlined"
            {...register("name", { required: true })}
            inputProps={{ maxLength: 50 }}
          />
          {errors.name?.message && (
            <ErrorMessage>{errors.name?.message}</ErrorMessage>
          )}
        </StyledFormControl>
        {/* Multi-select member field */}
        <StyledFormControl fullWidth>
          <StyledInputLabel>Select Team Members</StyledInputLabel>
          <Controller
            name="members"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={teamMembers}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => field.onChange(value)}
                value={Array.isArray(field.value) ? field.value : []}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      {option?.label}
                      {option?.email && (
                        <Typography
                          sx={{
                            color: `${theme.colors.gray1} !important`,
                            fontSize: "0.875rem",
                            fontWeight: "400",
                          }}
                        >
                          {option?.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select member"
                  />
                )}
              />
            )}
          />
          {errors.members?.message && (
            <ErrorMessage>{errors.members?.message}</ErrorMessage>
          )}
        </StyledFormControl>
        <StyledFormControl fullWidth>
          <Typography
            sx={{
              color: `${theme.colors.black2} !important`,
              fontSize: "1.125rem",
              fontWeight: "400",
            }}
          >
            Upload Image
          </Typography>
          <SquareContainer>
            <Input
              type="file"
              style={{ display: "none" }}
              inputRef={(e) => {
                profileImageInputRef.current = e;
              }}
              onChange={handleProfileImageSelect}
              inputProps={{ accept: ".png, .jpg, .jpeg" }}
            />
            {!selectedFile ? (
              <LogoUploadButton onClick={handleProfileUploadButtonClick}>
                <UploadImageIcon />
              </LogoUploadButton>
            ) : (
              <LogoUploadButtonAfter onClick={handleProfileUploadButtonClick}>
                <UploadImageIcon />
              </LogoUploadButtonAfter>
            )}
            {selectedFile && (
              <UploadedImage
                src={URL.createObjectURL(selectedFile)}
                alt="Uploaded Image"
              />
            )}
          </SquareContainer>
          {logoErrorMessage && <ErrorMessage>Logo is required</ErrorMessage>}
        </StyledFormControl>
      </CreateBoardWrapper>
      <Divider sx={{ borderColor: "#E3E6EA" }} />
      <StyledContainedButton
        variant="contained"
        size="small"
        sx={{
          margin: "1.875rem 1.875rem 1.875rem auto",
          padding: "0.313rem 2.375rem",
        }}
        onClick={handleSubmit(onSubmit)}
        disabled={loading}
        startIcon={loading && <CircularProgress size={20} color="inherit" />}
      >
        Create Team
      </StyledContainedButton>
    </StyledDialog>
  );
};

export default CreateTeamModal;
